import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ImagePlus, Loader2 } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { API_BASE } from '../../config/api';

const API = `${API_BASE}/api`;

// Generates Navigate-branded header images for every section (missing only, or replace all).
export default function GenerateAllImagesButton({ courseId, token, sections = [], onImage }) {
  const [open, setOpen] = useState(false);
  const [progress, setProgress] = useState(null);
  const missing = sections.filter((s) => !s.image_url).length;

  const run = async (replaceAll) => {
    setOpen(false);
    const targets = sections.map((s, i) => ({ s, i })).filter(({ s }) => replaceAll || !s.image_url);
    if (!targets.length) return toast.info('Every section already has an image');
    let done = 0, failed = 0;
    setProgress({ done, total: targets.length });
    const queue = [...targets];
    const worker = async () => {
      while (queue.length) {
        const { s, i } = queue.shift();
        try {
          const res = await axios.post(`${API}/ai/course/${courseId}/section/${i}/generate-image`,
            { section_title: s.title }, { headers: { Authorization: `Bearer ${token}` }, timeout: 120000 });
          if (res.data.image_url) onImage(i, res.data.image_url); else failed++;
        } catch (e) {
          failed++;
        }
        done++;
        setProgress({ done, total: targets.length });
      }
    };
    await Promise.all([worker(), worker(), worker()]);
    setProgress(null);
    if (failed) toast.warning(`${done - failed} images created, ${failed} failed`);
    else toast.success(`${done} section image${done === 1 ? '' : 's'} created`);
  };

  return (
    <>
      <button onClick={() => setOpen(true)} disabled={!!progress || !sections.length}
        className="w-full flex items-center justify-center gap-2 p-3 rounded-lg bg-navigate-gold hover:bg-navigate-gold-light text-black font-medium transition-colors disabled:opacity-60"
        data-testid="generate-all-section-images-btn">
        {progress ? <Loader2 className="h-4 w-4 animate-spin" /> : <ImagePlus className="h-4 w-4" />}
        {progress ? `Creating images… ${progress.done}/${progress.total}` : 'Generate images for all sections'}
      </button>
      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-zinc-950 border-zinc-800" data-testid="generate-all-images-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">Generate section images</DialogTitle>
            <DialogDescription className="text-zinc-400">
              NAVI creates a Navigate-branded header illustration from each section's content.
              {` ${missing} of ${sections.length} sections have no image yet.`}
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => run(true)} className="border-zinc-700 text-zinc-200" data-testid="generate-images-replace-all-btn">
              Replace all images
            </Button>
            <Button onClick={() => run(false)} disabled={!missing} className="bg-navigate-gold text-black hover:bg-navigate-gold-light" data-testid="generate-images-missing-only-btn">
              Only sections without an image
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
